import {useEffect, useRef, useState} from "react";
import {Alert, Box, Button, CircularProgress, Divider, IconButton, TextField, Typography} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import {Navigate, useNavigate} from "react-router-dom";
import {flushSync} from "react-dom";
import KioskHeader from "../components/KioskHeader";
import KioskSteps from "../components/KioskSteps";
import ProductCustomizer from "../components/ProductCustomizer";
import CouponScanDialog from "../components/CouponScanDialog";
import MobileNumberDialog from "../components/MobileNumberDialog";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {cartComboDiscount, cartTotal, describeItemOptions, getMeal, itemComboDiscount, itemLineTotal, itemUnitPrice} from "../orderUtils";
import {checkKioskCoupon, quoteKioskOrder} from "../api/kioskOrders";
import {useGlobalOrder} from "../../utils/GlobalStateContext";

const isMemberMobile = (value) => /^09\d{8}$/.test(value);

export default function CartPage({systemData, online, onHome}) {
  const navigate = useNavigate();
  const {state, dispatch} = useKioskSession();
  const {lang, tr, name} = useKioskLang();
  const {BarcodeScanner, barcode, setBarcode} = useGlobalOrder();
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [mobileDialog, setMobileDialog] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewSeq = useRef(0);

  const applyCoupon = async (rawCode) => {
    const code = String(rawCode ?? "").trim();
    if (!code || couponChecking) return;
    setCouponChecking(true);
    setCouponError("");
    try {
      const response = await checkKioskCoupon(code);
      if (response.error !== 0) {
        setCouponError(response.msg || tr("couponNotFound"));
        return;
      }
      if (response.coupon.status !== "usable") {
        setCouponError(tr("couponUnavailable", {status: response.coupon.status_text}));
        return;
      }
      dispatch({type: "SET_COUPON", code: response.coupon.code, info: response.coupon});
      setCouponInput("");
      setCouponOpen(false);
    } catch {
      setCouponError(tr("couponCheckFailed"));
    } finally {
      setCouponChecking(false);
    }
  };

  //掃碼槍掃轉盤券（TW# 開頭）直接套用
  useEffect(() => {
    if (barcode && /^tw#/i.test(String(barcode).trim())) {
      applyCoupon(barcode);
      setBarcode("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode]);

  //購物車即時試算優惠（套餐/飲料自動折扣＋折扣碼），preview 模式不驗手機/桌號、不產 quote_token
  useEffect(() => {
    if (!online || !state.sessionId || !state.cart.length) {
      setPreview(null);
      return undefined;
    }
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      try {
        const response = await quoteKioskOrder({
          session_id: state.sessionId,
          method: state.orderMethod,
          buycart: state.cart,
          coupon_code: state.couponCode,
          preview: 1,
        });
        if (seq !== previewSeq.current) return;
        if (response.error !== 0) {
          //折扣碼因購物車變動失效（如低於門檻）：移除並提示，其餘錯誤留給結帳時處理
          if (response.coupon_invalid) {
            setCouponError(response.msg || tr("couponCheckFailed"));
            dispatch({type: "CLEAR_COUPON"});
          }
          setPreview(null);
          return;
        }
        setPreview(response.pricing);
      } catch {
        if (seq === previewSeq.current) setPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cart, state.couponCode, state.orderMethod, online]);

  if (!state.sessionId) return <Navigate to="/kiosk/welcome" replace/>;
  const total = cartTotal(systemData, state.cart);
  //套餐優惠已直接寫進品項價格（見 orderUtils.itemComboDiscount），前端沒連到後端試算也先扣
  const comboDiscount = cartComboDiscount(systemData, state.cart);
  const mealsAmount = total - comboDiscount;
  //試算的小計須與目前購物車一致才採用，避免顯示到修改前購物車的折扣
  const previewValid = preview && Number(preview.subtotal) === total;
  //摘要的折抵列只顯示訂單層級優惠（折扣碼、跨品項自動折扣），套餐部分已含在品項價格
  const orderDiscount = previewValid ? Math.max(0, Math.abs(Number(preview.discount ?? 0)) - comboDiscount) : 0;
  const payable = previewValid ? Number(preview.total) : mealsAmount;
  const count = state.cart.reduce((sum, item) => sum + Number(item.qty), 0);
  const isTakeout = Number(state.orderMethod) === 2;
  const isDineIn = Number(state.orderMethod) === 3;
  const tableOk = !isDineIn || Boolean(state.tableNumber);
  const canCheckout = online && state.cart.length > 0 && !loading && tableOk;

  //外帶以手機號碼作為取餐依據：未填時按「下一步」跳數字盤彈窗（同內用桌號），填完自動續行結帳
  const checkout = async (mobileOverride) => {
    if (!canCheckout) return;
    const mobile = mobileOverride ?? state.memberMobile;
    if (isTakeout && !isMemberMobile(mobile)) {
      setMobileDialog({continueCheckout: true});
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await quoteKioskOrder({
        session_id: state.sessionId,
        method: state.orderMethod,
        buycart: state.cart,
        expected_total: total,
        member_mobile: mobile,
        table_number: state.tableNumber,
        coupon_code: state.couponCode,
      });
      if (response.error !== 0) {
        setError(response.msg || tr("cartChanged"));
        if (response.coupon_invalid) dispatch({type: "CLEAR_COUPON"});
        return;
      }
      flushSync(() => dispatch({type: "SET_PRICING", pricing: response.pricing}));
      navigate("/kiosk/payment");
    } catch {
      setError(tr("quoteFailed"));
    } finally {
      setLoading(false);
    }
  };

  return <Box sx={{height: "100dvh", display: "flex", flexDirection: "column", bgcolor: "#F7F1E8"}}>
    <BarcodeScanner/>
    <KioskHeader systemData={systemData} onHome={onHome} method={state.orderMethod} tableNumber={state.tableNumber} cartCount={count} onBack={() => navigate("/kiosk/menu")} online={online}/>
    <KioskSteps active={1}/>
    <Box sx={{flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1fr 390px", gap: 2.5, p: 2.5}}>
      <Box sx={{overflow: "auto", pr: 1}}>
        <Box sx={{display: "flex", alignItems: "baseline", gap: 1.5, mb: 1.5}}>
          <Typography sx={{fontSize: 30, fontWeight: 950}}>{tr("reviewCart")}</Typography>
          {count > 0 && <Typography color="text.secondary" sx={{fontSize: 17, fontWeight: 800}}>{tr("totalCount", {n: count})}</Typography>}
        </Box>
        {!state.cart.length && (
          <Box sx={{p: 6, bgcolor: "#FFF", borderRadius: 1.5, border: "1px solid rgba(80,55,40,.12)", textAlign: "center"}}>
            <RestaurantMenuRoundedIcon sx={{fontSize: 60, color: "#B39D8D"}}/>
            <Typography sx={{fontSize: 23, fontWeight: 900, mt: 1}}>{tr("emptyCart")}</Typography>
            <Typography color="text.secondary" sx={{fontSize: 16, mt: .5}}>{tr("emptyCartText")}</Typography>
            <Button sx={{mt: 2.5, minWidth: 220}} variant="contained" onClick={() => navigate("/kiosk/menu")}>{tr("startShopping")}</Button>
          </Box>
        )}
        <Box sx={{display: "grid", gap: 1.5}}>
          {state.cart.map((item) => {
            const meal = getMeal(systemData, item.meal_id);
            const imageUrl = meal?.image_url || meal?.image;
            const options = describeItemOptions(systemData, item, lang);
            const lineGross = itemUnitPrice(systemData, item) * item.qty;
            const lineDiscount = itemComboDiscount(systemData, item);
            const lineTotal = itemLineTotal(systemData, item);
            return <Box key={item.lineId} sx={{p: 1.75, bgcolor: "#FFF", borderRadius: 1.5, border: "1px solid rgba(80,55,40,.12)", display: "flex", gap: 1.75}}>
              <Box sx={{width: 88, height: 88, flex: "0 0 88px", borderRadius: 1, overflow: "hidden", bgcolor: "#E9DACB", display: "grid", placeItems: "center"}}>
                {imageUrl ? <Box component="img" src={imageUrl} alt={name(meal)} onError={(event) => {event.currentTarget.style.display = "none";}} sx={{width: "100%", height: "100%", objectFit: "cover"}}/> : <ImageNotSupportedRoundedIcon sx={{fontSize: 32, color: "#B39D8D"}}/>}
              </Box>
              <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column"}}>
                <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                  <Typography sx={{fontSize: 21, fontWeight: 950, flex: 1}}>{name(meal) || tr("meals")}</Typography>
                  <IconButton aria-label={tr("removeItem", {name: name(meal) || tr("meals")})} onClick={() => dispatch({type: "REMOVE_ITEM", lineId: item.lineId})} sx={{color: "text.secondary"}}><DeleteOutlineRoundedIcon/></IconButton>
                </Box>
                {options.length > 0 && <Typography color="text.secondary" sx={{fontSize: 16, mt: .3}}>{options.join("、")}</Typography>}
                {item.note && <Typography sx={{fontSize: 15.5, mt: .3, color: "primary.main"}}>{tr("notePrefix")}{item.note}</Typography>}
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mt: "auto", pt: 1.25}}>
                  <Button size="small" variant="outlined" startIcon={<EditRoundedIcon/>} onClick={() => setEditing({item, meal})} sx={{minHeight: 42, borderRadius: 1}}>{tr("editOptions")}</Button>
                  <Box sx={{ml: "auto", display: "flex", alignItems: "center", bgcolor: "#F7F1E8", borderRadius: 1}}>
                    <IconButton aria-label={tr("decreaseQty")} disabled={item.qty <= 1} onClick={() => dispatch({type: "SET_QUANTITY", lineId: item.lineId, qty: item.qty - 1})} sx={{width: 44, height: 44}}><RemoveRoundedIcon/></IconButton>
                    <Typography sx={{fontSize: 20, fontWeight: 900, width: 34, textAlign: "center"}}>{item.qty}</Typography>
                    <IconButton aria-label={tr("increaseQty")} onClick={() => dispatch({type: "SET_QUANTITY", lineId: item.lineId, qty: item.qty + 1})} sx={{width: 44, height: 44}}><AddRoundedIcon/></IconButton>
                  </Box>
                  <Box sx={{minWidth: 88, textAlign: "right"}}>
                    {lineDiscount > 0 && <Typography sx={{fontSize: 14, fontWeight: 800, color: "#2E7D32"}}>{tr("comboDiscountTag")} -${lineDiscount.toLocaleString()}<Box component="span" sx={{ml: .75, color: "text.secondary", textDecoration: "line-through", fontWeight: 700}}>${lineGross.toLocaleString()}</Box></Typography>}
                    <Typography sx={{fontSize: 22, fontWeight: 950}}>${lineTotal.toLocaleString()}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>;
          })}
        </Box>
        {state.cart.length > 0 && <Button fullWidth onClick={() => navigate("/kiosk/menu")} sx={{mt: 1.5, minHeight: 56, border: "2px dashed rgba(80,55,40,.25)", borderRadius: 1.5, color: "text.secondary"}}>{tr("addMore")}</Button>}
      </Box>
      <Box sx={{overflow: "auto", display: "grid", gap: 1.5, alignContent: "start"}}>
        <Box sx={{p: 2.5, bgcolor: "#FFF", borderRadius: 1.5, border: "1px solid rgba(80,55,40,.12)", boxShadow: "0 10px 28px rgba(76,49,32,.07)"}}>
          <Typography sx={{fontSize: 22, fontWeight: 950}}>{tr("orderSummary")}</Typography>
          <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5}}>
            <Typography sx={{fontSize: 17}}>{tr("serviceMode")}</Typography>
            <Button size="small" variant="outlined" onClick={() => navigate("/kiosk/service")} sx={{minHeight: 40, borderRadius: 1}}>{isDineIn ? tr("dineIn") : tr("takeout")} · {tr("edit")}</Button>
          </Box>
          {isDineIn && state.tableNumber && (
            <Box sx={{display: "flex", justifyContent: "space-between", mt: 1.25}}>
              <Typography sx={{fontSize: 17}}>{tr("tableNumber")}</Typography>
              <Typography sx={{fontSize: 17, fontWeight: 800}}>{tr("tableChip", {n: state.tableNumber})}</Typography>
            </Box>
          )}
          {isTakeout && (
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25}}>
              <Typography sx={{fontSize: 17}}>{tr("pickupMobileLabel")}</Typography>
              <Button size="small" variant="outlined" onClick={() => setMobileDialog({continueCheckout: false})} sx={{minHeight: 40, borderRadius: 1, ...(!state.memberMobile && {color: "text.secondary", borderColor: "rgba(80,55,40,.25)"})}}>
                {state.memberMobile ? `${state.memberMobile} · ${tr("edit")}` : tr("notEntered")}
              </Button>
            </Box>
          )}
          {!isTakeout && state.memberMobile && (
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25}}>
              <Typography sx={{fontSize: 17}}>{tr("pointsMobileLabel")}</Typography>
              <Box sx={{display: "flex", alignItems: "center", gap: .25}}>
                <Button size="small" variant="outlined" onClick={() => setMobileDialog({continueCheckout: false})} sx={{minHeight: 40, borderRadius: 1}}>{state.memberMobile} · {tr("edit")}</Button>
                <IconButton size="small" aria-label={tr("cancel")} onClick={() => dispatch({type: "SET_MEMBER_MOBILE", mobile: ""})}><CloseRoundedIcon sx={{fontSize: 19}}/></IconButton>
              </Box>
            </Box>
          )}
          <Box sx={{display: "flex", justifyContent: "space-between", mt: 1.25}}>
            <Typography sx={{fontSize: 17}}>{tr("mealCount")}</Typography>
            <Typography sx={{fontSize: 17, fontWeight: 800}}>{tr("itemsCount", {n: count})}</Typography>
          </Box>
          <Divider sx={{my: 1.75}}/>
          {comboDiscount > 0 && (
            <Box sx={{display: "flex", justifyContent: "space-between", mb: 1.25}}>
              <Typography sx={{fontSize: 15, color: "text.secondary"}}>{tr("comboDiscountIncluded")}</Typography>
              <Typography sx={{fontSize: 15, fontWeight: 800, color: "#2E7D32"}}>-${comboDiscount.toLocaleString()}</Typography>
            </Box>
          )}
          {orderDiscount > 0 && <>
            <Box sx={{display: "flex", justifyContent: "space-between"}}>
              <Typography sx={{fontSize: 17}}>{tr("mealsSubtotal")}</Typography>
              <Typography sx={{fontSize: 17, fontWeight: 800}}>${mealsAmount.toLocaleString()}</Typography>
            </Box>
            <Box sx={{display: "flex", justifyContent: "space-between", mt: 1.25, mb: 1.5}}>
              <Typography sx={{fontSize: 17, fontWeight: 800, color: "#2E7D32"}}>{tr("orderDiscountDeduct")}</Typography>
              <Typography sx={{fontSize: 17, fontWeight: 900, color: "#2E7D32"}}>-${orderDiscount.toLocaleString()}</Typography>
            </Box>
          </>}
          {/* 折扣碼與集點走低調路線：預設只有文字按鈕，點了才展開輸入 */}
          {state.couponInfo && (
            <Box sx={{mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: "#EFF7EE", border: "1px solid #BBD8B9", display: "flex", alignItems: "center", gap: 1}}>
              <CardGiftcardRoundedIcon sx={{fontSize: 20, color: "#2E7D32"}}/>
              <Typography sx={{flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 900, color: "#2E7D32"}} noWrap>{state.couponInfo.name}</Typography>
              <IconButton size="small" aria-label={tr("removeCoupon")} onClick={() => dispatch({type: "CLEAR_COUPON"})}><CloseRoundedIcon sx={{fontSize: 18}}/></IconButton>
            </Box>
          )}
          {!state.couponInfo && couponOpen && (
            <Box sx={{mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: "#FAF5EE", border: "1px dashed rgba(80,55,40,.3)"}}>
              <Box sx={{display: "flex", alignItems: "center"}}>
                <Typography sx={{flex: 1, fontSize: 15.5, fontWeight: 900}}>{tr("coupon")}</Typography>
                <IconButton size="small" aria-label={tr("cancel")} onClick={() => {setCouponOpen(false); setCouponError("");}}><CloseRoundedIcon sx={{fontSize: 18}}/></IconButton>
              </Box>
              <Button fullWidth variant="outlined" startIcon={<QrCodeScannerRoundedIcon/>} disabled={couponChecking} onClick={() => setScanOpen(true)} sx={{mt: 1, minHeight: 46}}>
                {couponChecking ? <CircularProgress size={20} color="inherit"/> : tr("scanCoupon")}
              </Button>
              <Box sx={{mt: 1, display: "flex", gap: 1}}>
                <TextField size="small" fullWidth placeholder={tr("couponInputHint")} value={couponInput} onChange={(event) => {setCouponInput(event.target.value); setCouponError("");}} onKeyDown={(event) => {if (event.key === "Enter") applyCoupon(couponInput);}}/>
                <Button variant="outlined" disabled={!couponInput.trim() || couponChecking} onClick={() => applyCoupon(couponInput)} sx={{minWidth: 76, minHeight: 40}}>{tr("apply")}</Button>
              </Box>
            </Box>
          )}
          {((!state.couponInfo && !couponOpen) || (!isTakeout && !state.memberMobile)) && (
            <Box sx={{display: "flex", gap: 2.5, mb: 1.25}}>
              {!state.couponInfo && !couponOpen && (
                <Button size="small" onClick={() => {setCouponOpen(true); setCouponError("");}} sx={{p: 0, minWidth: 0, fontSize: 15, fontWeight: 700, color: "text.secondary", textDecoration: "underline", textUnderlineOffset: 4, "&:hover": {bgcolor: "transparent", color: "text.primary", textDecoration: "underline"}}}>{tr("haveCoupon")}</Button>
              )}
              {!isTakeout && !state.memberMobile && (
                <Button size="small" onClick={() => setMobileDialog({continueCheckout: false})} sx={{p: 0, minWidth: 0, fontSize: 15, fontWeight: 700, color: "text.secondary", textDecoration: "underline", textUnderlineOffset: 4, "&:hover": {bgcolor: "transparent", color: "text.primary", textDecoration: "underline"}}}>{tr("wantPoints")}</Button>
              )}
            </Box>
          )}
          {couponError && <Typography sx={{fontSize: 14, color: "error.main", mb: 1, ...(couponOpen && {mt: -1})}}>{couponError}</Typography>}
          <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "baseline"}}>
            <Typography sx={{fontSize: 20, fontWeight: 900}}>{tr("total")}</Typography>
            <Typography sx={{fontSize: 34, fontWeight: 950, color: "primary.main"}}>${payable.toLocaleString()}</Typography>
          </Box>
          {!online && <Alert severity="warning" sx={{mt: 1.5}}>{tr("offlineCheckout")}</Alert>}
          {error && <Alert severity="error" sx={{mt: 1.5}}>{error}</Alert>}
          <Button fullWidth variant="contained" onClick={() => checkout()} disabled={!canCheckout} endIcon={loading ? null : <ArrowForwardRoundedIcon sx={{animation: "kioskArrow 1.2s infinite"}}/>} sx={{mt: 2.5, minHeight: 72, fontSize: 22, animation: canCheckout ? "kioskPulse 2s infinite" : "none"}}>
            {loading ? <CircularProgress size={30} color="inherit"/> : tr("nextToPayment")}
          </Button>
          <Typography color="text.secondary" sx={{fontSize: 14, mt: 1.25, textAlign: "center"}}>{tr("checkoutHint")}</Typography>
        </Box>
      </Box>
    </Box>
    <ProductCustomizer open={Boolean(editing)} meal={editing?.meal} item={editing?.item} systemData={systemData} onClose={() => setEditing(null)} onSave={(item) => {dispatch({type: "UPDATE_ITEM", item}); setEditing(null);}}/>
    <CouponScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onDetect={(code) => {setScanOpen(false); applyCoupon(code);}}/>
    <MobileNumberDialog
      open={Boolean(mobileDialog)}
      hint={isTakeout ? tr("pickupMobileHint") : tr("memberPointsHint")}
      initial={state.memberMobile}
      onClose={() => setMobileDialog(null)}
      onConfirm={(mobile) => {
        dispatch({type: "SET_MEMBER_MOBILE", mobile});
        const shouldContinue = mobileDialog?.continueCheckout;
        setMobileDialog(null);
        if (shouldContinue) checkout(mobile);
      }}
    />
  </Box>;
}
